<template>
  <div class="asset-map sdaa-map">
    <!-- 통합된 필터 섹션 -->
    <div class="unified-filter-section" v-if="!dashboardMode">
      <div class="unified-filter-buttons">
        <!-- 병동 선택 버튼들 -->
        <div class="ward-filter-row">
          <button :class="{active: sdaaFloor === '73'}" @click="sdaaFloor = '73'" class="ward-btn">73병동</button>
          <button :class="{active: sdaaFloor === '74'}" @click="sdaaFloor = '74'" class="ward-btn">74병동</button>
          <button :class="{active: sdaaFloor === '83'}" @click="sdaaFloor = '83'" class="ward-btn">83병동</button>
        </div>
        
        <!-- 디바이스 타입 필터 버튼들 -->
        <div class="device-filter-row">
          <button 
            @click="toggleDeviceFilter('DVT')" 
            class="filter-btn"
            :class="{ active: selectedDeviceFilters.includes('DVT') }"
          >
            DVT
          </button>
          <button 
            @click="toggleDeviceFilter('VarioS')" 
            class="filter-btn"
            :class="{ active: selectedDeviceFilters.includes('VarioS') }"
          >
            VarioS
          </button>
          <button 
            @click="toggleDeviceFilter('Nebulizer')" 
            class="filter-btn"
            :class="{ active: selectedDeviceFilters.includes('Nebulizer') }"
          >
            Nebulizer
          </button>
          <button 
            @click="toggleDeviceFilter('InfusionP')" 
            class="filter-btn"
            :class="{ active: selectedDeviceFilters.includes('InfusionP') }"
          >
            InfusionP
          </button>
          <button 
            @click="toggleDeviceFilter('PatientM')" 
            class="filter-btn"
            :class="{ active: selectedDeviceFilters.includes('PatientM') }"
          >
            PatientM
          </button>
          <button 
            @click="toggleDeviceFilter('FeedingP')" 
            class="filter-btn"
            :class="{ active: selectedDeviceFilters.includes('FeedingP') }"
          >
            FeedingP
          </button>
          <button 
            @click="toggleDeviceFilter('PulseOxi')" 
            class="filter-btn"
            :class="{ active: selectedDeviceFilters.includes('PulseOxi') }"
          >
            PulseOxi
          </button>
          <button 
            @click="toggleDeviceFilter('SCD_P')" 
            class="filter-btn"
            :class="{ active: selectedDeviceFilters.includes('SCD_P') }"
          >
            SCD_P
          </button>
          <button 
            @click="toggleDeviceFilter('HPO')" 
            class="filter-btn"
            :class="{ active: selectedDeviceFilters.includes('HPO') }"
          >
            HPO
          </button>
          <button @click="clearDeviceFilters" class="filter-btn clear-btn">All</button>
        </div>
      </div>
    </div>
    <div class="asset-map-flex-col">
      <div class="map-container" ref="mapContainer">
        <img 
          :src="backgroundImage" 
          alt="Background" 
          class="background-image sdaa-image"
          @load="onImageLoad"
          @error="onImageError"
          ref="backgroundImg"
        />
        <div
          v-for="([room, coord]) in filteredRoomCoords"
          :key="room"
          class="room-box"
          :style="{ left: `${coord.x}px`, top: `${coord.y}px` }"
        >
          <!-- 기존 방 박스 내용 (필요시 추가) -->
        </div>
        <div
          v-for="room in roomGroups"
          :key="room.name"
          class="room-marker-group"
          :style="{ left: `${room.x}px`, top: `${room.y}px` }"
          @click="handleRoomClick(room)"
        >
          <div class="room-label" 
               @mouseenter="showRoomTooltip($event, room)"
               @mouseleave="hideTooltip"
               @touchstart.stop="showRoomTooltip($event.changedTouches ? { clientX: $event.changedTouches[0].clientX, clientY: $event.changedTouches[0].clientY } : $event, room)"
               @click.stop="handleRoomClick(room)">
            {{ room.name }}
          </div>
          <!-- 장비 이름들을 플로팅으로 표시 -->
          <div class="device-names-floating">
            <template v-for="(device, idx) in room.deviceNames" :key="device.name">
              <div
                v-if="device.name !== 'temp'"
                class="device-name-tag"
                :style="{
                  backgroundColor: getDeviceColor(device.type),
                  left: `${device.positionX}px`,
                  top: `${device.positionY}px`,
                  width: `${device.width}px`,
                  zIndex: idx + 1
                }"
                :title="`${device.originalName} (${device.type})`"
                @mouseenter="showDeviceTooltip($event, { name: device.type, devicenames: [device.originalName] }, room)"
                @touchstart.stop="showDeviceTooltip($event.changedTouches ? { clientX: $event.changedTouches[0].clientX, clientY: $event.changedTouches[0].clientY } : $event, { name: device.type, devicenames: [device.originalName] }, room)"
                @mouseleave="hideTooltip"
                @click.stop="handleRoomClick(room)"
              >
                {{ device.name }}
              </div>
            </template>
            <span v-if="room.deviceNames.length > 6" class="device-names-ellipsis">...</span>
          </div>
        </div>
      </div>
      <!-- 표가 맵 아래에 고정, 대시보드 모드에서는 숨김 -->
      <div class="tags-info-section" v-if="!dashboardMode">
        <div>대시보드 모드 아님 - 표가 보임</div>
        <div>dashboardMode: {{ dashboardMode }}</div>
        <div
          class="floor-group"
          v-for="group in groups"
          :key="group.name"
        >
          <h3>{{ group.name }} ({{ group.tags.length }})</h3>
          <ul>
            <li
              v-for="tag in group.tags"
              :key="tag.tag_mac"
            >
              {{ tag.devicename }}
            </li>
          </ul>
        </div>
      </div>
    </div>
    
    
    <!-- 툴팁 -->
    <div
      v-if="tooltip.show"
      class="marker-tooltip"
      :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
    >
      <!-- 방 라벨 위에 마우스 오버 시: 방 이름 + 모든 디바이스 정보 -->
      <div v-if="tooltip.room" class="tooltip-room-name"><b>{{ tooltip.room.name }}</b></div>
      <div class="tooltip-device-list">
        <!-- 마커(동그라미) 위에 마우스 오버 시: devicename 리스트 -->
        <template v-if="!tooltip.room && tooltip.info && tooltip.info[0] && tooltip.info[0].devicenames">
          <div class="tooltip-device-item" style="font-weight:bold; margin-bottom:2px;">
            {{ tooltip.info[0].name }}
          </div>
          <div v-for="devicename in tooltip.info[0].devicenames" :key="devicename" class="tooltip-device-item">
            {{ devicename }}
          </div>
        </template>
        <!-- 방 라벨 위에 마우스 오버 시: 기존 devicetype별 개수 -->
        <template v-else>
          <div v-for="type in tooltip.info" :key="type.name" class="tooltip-device-item">
            <span class="tooltip-device-color" :style="{ backgroundColor: getDeviceColor(type.name) }"></span>
            <span class="tooltip-device-name">{{ type.name }}</span>
            <span class="tooltip-device-count">({{ type.count }})</span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    dashboardMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sdaaFloor: '73',
      displayedTags: [],
      animatingTags: [],
      imageLoaded: false,
      containerWidth: 0,
      containerHeight: 0,
      imageNaturalWidth: 0,
      imageNaturalHeight: 0,
      groups: [],
      tooltip: {
        show: false,
        x: 0,
        y: 0,
        room: null,
        info: null
      },
      selectedDeviceFilters: [], // 선택된 디바이스 타입 필터들
      deviceTypeColors: {
        DVT: '#00bcd4',
        VarioS: '#ff9800',
        Nebulizer: '#8bc34a',
        InfusionP: '#ff4da6',
        PatientM: '#9c27b0',
        FeedingP: '#ffc107',
        PulseOxi: '#2196f3',
        SCD_P: '#4caf50',
        HPO: '#f44336'
      },
      roomGroups: [],
      roomCoords: {
        // 73 - 이미지와 맞게 좌표 조정
        '3701': { x: -180, y: 30 }, //1510
        '3703': { x: -50, y: 30 }, //1380
        '3705': { x: 80, y: 30 }, //1255
        '3707': { x: 230, y: 30 }, //1130
        '3709': { x: 380, y: 30 }, //1000
        '73보관실': { x: 550, y: 30 }, //850 
        '73간호사실': { x: 850, y: 30 }, //850 
        '3702': { x: -110, y: 310 },   //1480
        '3704': { x: -10, y: 310 }, //1380
        '3706': { x: 75, y: 310 }, //1310
        '3708': { x: 145, y: 310 },  //1240
        '3710': { x: 220, y: 310 },    //1110
        '3712': { x: 380, y: 310 },  //980
        // 74 - 이미지와 맞게 좌표 조정
        '7층_간이주방': { x: 850, y: 310 }, // 530

        '74보관실': { x: 550, y: 30 }, // 530
        '74간호사실': { x: 850, y: 30 }, // 850 
        '3751': { x: 950, y: 30 }, // 360
        '3753': { x: 1090, y: 30 }, // 215
        '3755': { x: 1230, y: 30 }, // 100
        '3757': { x: 1380, y: 30 }, // -40
        '3759': { x: 1500, y: 30 }, // -140

        '3752': { x: 950, y: 310 }, // 360
        '3754': { x: 1090, y: 310 }, // 220
        '3756': { x: 1230, y: 310 }, // 145
        '3758': { x: 1300, y: 310 }, // 80
        '3760': { x: 1375, y: 310 }, // 5
        '3762': { x: 1450, y: 310 }, // -120
        
        // 83 - 이미지와 맞게 좌표 조정
        '3801': { x: -180, y: 35 },
        '3803': { x: -110, y: 35 },
        '3805': { x: -30, y: 35 },
        '3807': { x: 50, y: 35 },
        '3809': { x: 120, y: 35 },
        '3811': { x: 180, y: 35 },
        '3813': { x: 260, y: 35 },
        '3815': { x: 340, y: 35 },
        '3817': { x: 400, y: 35 },
        '3819': { x: 480, y: 35 },
        '83보관실': { x: 600, y: 35 },
        '3802': { x: -110, y: 310 },   // 3802를 3801과 같은 높이로 조정
        '3804': { x: 40, y: 310 },  // 3804를 3803과 같은 높이로 조정
        '3806': { x: 110, y: 310 },  // 3806을 3805와 같은 높이로 조정
        '3808': { x: 195, y: 310 },  // 3808을 3807과 같은 높이로 조정
        '3810': { x: 260, y: 310 },  // 3810을 3809와 같은 높이로 조정
        '3812': { x: 330, y: 310 },  // 3812를 3811과 같은 높이로 조정
        '3814': { x: 400, y: 310 },  // 3814를 38
        '3816': { x: 480, y: 310 },  // 3816을 3815와 같은 높이로 조정

      
        '83간호사실': { x: 750, y: 35 },
        '3821': { x: 830, y: 35 },
        '3823': { x: 905, y: 35 },
        '3825': { x: 975, y: 35 },
        '3827': { x: 1040, y: 35 },
        '3829': { x: 1110, y: 35 },
        '3831': { x: 1240, y: 35 },
        '3833': { x: 1380, y: 35 },
        '3835': { x: 1580, y: 35 },
        
        '3822': { x: 975, y: 310 }, // 3822를 3821과 같은 높이로 조정
        '3824': { x: 1040, y: 310 }, // 3824를 3823과 같은 높이로 조정
        '3826': { x: 1110, y: 310 }, // 3826을 3825와 같은 높이로 조정
        '3828': { x: 1180, y: 310 }, // 3828을 3827과 같은 높이로 조정
        '3830': { x: 1250, y: 310 }, // 3830을 3829와 같은 높이로 조정
        '3832': { x: 1315, y: 310 }, // 3832를 3831과 같은 높이로 조정
        '3834': { x: 1380, y: 310 }, // 3834를 3833과 같은 높이로 조정
        '3836': { x: 1460, y: 310 }, // 3836을 3835와 같은 높이로 조정
        
        // ... 필요시 추가 ...
      },
      resizeTimeout: null,
    };
  },
  computed: {
    backgroundImage() {
      const imagePath = this.sdaaFloor === '83'
        ? require('@/assets/AssetMap_SDAA_08.png')
        : require('@/assets/AssetMap_SDAA_07.png');
      
      console.log('Background image path:', imagePath, 'for floor:', this.sdaaFloor);
      return imagePath;
    },
    filteredDisplayedTags() {
      if (this.sdaaFloor === '73') {
        return this.displayedTags.filter(tag => tag.devicename && tag.devicename.startsWith('73'));
      } else if (this.sdaaFloor === '74') {
        return this.displayedTags.filter(tag => tag.devicename && tag.devicename.startsWith('74'));
      } else if (this.sdaaFloor === '83') {
        return this.displayedTags.filter(tag => tag.devicename && tag.devicename.startsWith('83'));
      }
      return this.displayedTags;
    },
    imageScale() {
      if (!this.imageLoaded || !this.$refs.mapContainer || !this.$refs.backgroundImage) {
        return { scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0 };
      }
      
      const container = this.$refs.mapContainer;
      const image = this.$refs.backgroundImage;
      
      if (!container || !image) {
        return { scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0 };
      }
      
      // 컨테이너와 이미지의 실제 크기
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const imageWidth = image.clientWidth;
      const imageHeight = image.clientHeight;
      
      // 스케일 계산 (이미지가 컨테이너에 맞춰진 비율)
      const scaleX = imageWidth / this.imageNaturalWidth;
      const scaleY = imageHeight / this.imageNaturalHeight;
      
      // 오프셋 계산 (이미지가 컨테이너 중앙에 위치할 때의 오프셋)
      const offsetX = (containerWidth - imageWidth) / 2;
      const offsetY = (containerHeight - imageHeight) / 2;
      
      // 디버깅을 위한 콘솔 로그
      console.log('Image Scale Debug:', {
        containerWidth,
        containerHeight,
        imageWidth,
        imageHeight,
        imageNaturalWidth: this.imageNaturalWidth,
        imageNaturalHeight: this.imageNaturalHeight,
        scaleX,
        scaleY,
        offsetX,
        offsetY
      });
      
      return { scaleX, scaleY, offsetX, offsetY };
    },
    filteredRoomCoords() {
      const rooms7 = [
        '3701','3702','3703','3704','3705','3706','3707','3708','3709','3710','3712','3751','3752','3753','3754','3755','3756','3757','3758','3759','3760','3762',
        '73보관실','73간호사실','74보관실','74간호사실','7층_간이주방'
      ];
      const rooms8 = [
        '3801','3802','3803','3804','3805','3806','3807','3808','3809','3810',
        '3811','3812','3813','3814','3815','3816','3817','3818','3819','3820',
        '3821','3822','3823','3824','3825','3826','3827','3828','3829','3830',
        '3831','3832','3833','3834','3835','3836','83보관실', '83간호사실'
      ];
      const target = (this.sdaaFloor === '73' || this.sdaaFloor === '74') ? rooms7 : rooms8;
      return Object.entries(this.roomCoords).filter(([room]) => target.includes(room));
    }
  },
  methods: {
    showRoomTooltip(event, room) {
      this.tooltip = {
        show: true,
        x: event.clientX + 10,
        y: event.clientY - 10,
        room,
        info: room.displayTypes // 방 라벨 위에 마우스 오버 시 방 전체 정보 표시
      };
    },
    showDeviceTooltip(event, type, room) {
      // 해당 room의 tags에서 type.name과 일치하는 tag.devicename 리스트 추출
      const devicenames = room.tags
        .filter(tag => tag.devicetype === type.name)
        .map(tag => tag.devicename)
        .filter(name => name && name !== 'temp');
      this.tooltip = {
        show: true,
        x: event.clientX + 10,
        y: event.clientY - 10,
        room: null,
        info: [{ name: type.name, devicenames }], // devicename 리스트만 전달
      };
    },
    hideTooltip() {
      this.tooltip.show = false;
    },
    async fetchTagData() {
      try {
        console.log('fetchTagData called with filters:', {
          sdaaFloor: this.sdaaFloor,
          selectedDeviceFilters: this.selectedDeviceFilters
        });
        
        const response = await axios.get(`/api/assetMapTagList`, { params: { company: 'SDAA' } });
        let newTags = response.data;
        
        // roomCoords에 정의된 모든 방에 대해 temp tag 추가
        Object.keys(this.roomCoords).forEach(room => {
          if (!newTags.some(tag => tag.taglocation === room)) {
            newTags.push({
              taglocation: room,
              devicetype: 'temp',
              devicename: 'temp',
              tag_mac: `temp_${room}`
            });
          }
        });
        // taglocation(방번호) 기준으로 그룹핑
        const roomMap = {};
        newTags.forEach(tag => {
          const room = tag.taglocation || '기타';
          if (!roomMap[room]) roomMap[room] = [];
          roomMap[room].push(tag);
        });
        
        // roomGroups 생성: location별 devicetype별 카운트
        console.log('Filtering rooms for floor:', this.sdaaFloor);
        
        this.roomGroups = Object.entries(roomMap)
          .filter(([room]) => {
            // 73병동: 3701~3712, 73보관실만
            const rooms73 = [
              '3701','3702','3703','3704','3705','3706','3707','3708','3709','3710','3712','73보관실','7층_간이주방','73간호사실'
            ];
            // 74병동: 3751~3762, 74보관실, 간이주방만
            const rooms74 = [
              '3751','3752','3753','3754','3755','3756','3757','3758','3759','3760','3761','3762','74보관실','7층_간이주방','74간호사실'
            ];
            // 83병동: 3801~3836, 83보관실, 83간호사실만
            const rooms83 = [
              '3801','3802','3803','3804','3805','3806','3807','3808','3809','3810',
              '3811','3812','3813','3814','3815','3816','3817','3818','3819','3820',
              '3821','3822','3823','3824','3825','3826','3827','3828','3829','3830',
              '3831','3832','3833','3834','3835','3836','83보관실', '83간호사실'
            ];
            
            let isIncluded = false;
            if (this.sdaaFloor === '73') {
              isIncluded = rooms73.includes(room);
            } else if (this.sdaaFloor === '74') {
              isIncluded = rooms74.includes(room);
            } else if (this.sdaaFloor === '83') {
              isIncluded = rooms83.includes(room);
            }
            
            if (isIncluded) {
              console.log('Room included:', room);
            }
            
            return isIncluded;
          })
          .map(([room, tags]) => {
            // 디바이스 타입 필터 적용
            let filteredTags = tags;
            if (this.selectedDeviceFilters.length > 0) {
              filteredTags = tags.filter(tag => {
                if (tag.devicetype === 'temp') return true; // temp는 항상 포함
                return this.selectedDeviceFilters.includes(tag.devicetype);
              });
            }
            
            // devicetype별 그룹핑 (기존 유지)
            const typeCount = {};
            filteredTags.forEach(tag => {
              const type = tag.devicetype || '기타';
              if (!typeCount[type]) typeCount[type] = 0;
              typeCount[type]++;
            });
            const displayTypes = Object.entries(typeCount).map(([name, count]) => ({ name, count }));
            
            // 장비 이름들을 배열로 생성 (플로팅용) - 3글자_숫자 형식으로 변경
            const deviceNames = filteredTags
              .filter(tag => tag.devicename && tag.devicename !== 'temp')
              .map(tag => {
                // 원본 이름에서 접두사 제거 후 3글자_숫자 형식으로 변환
                const originalName = tag.devicename.replace(/^(73_|74_|83_)/, '');
                const shortName = this.convertToShortName(originalName, tag.devicetype);
                
                return {
                  name: shortName,
                  type: tag.devicetype || '기타',
                  originalName: tag.devicename, // 원본 이름 보존
                  width: shortName.length * 8 + 16 // 짧은 이름 길이에 따른 너비 계산
                };
              }); // slice(0, 8) 제거 - 모든 장비 표시
            
            // 이미지 원본(1080x1920) 기준 roomCoords 좌표를 스케일링하여 사용
            const base = this.roomCoords[room] || { x: 500, y: 500 };
            const scale = this.imageScale;
            const x = base.x * scale.scaleX + scale.offsetX; // 스케일링된 x 좌표
            const y = base.y * scale.scaleY + scale.offsetY; // 스케일링된 y 좌표
            
            // 디버깅을 위한 콘솔 로그
            console.log(`Room ${room} coordinates:`, {
              original: base,
              scale: scale,
              final: { x, y }
            });
            
            // 스마트 배치를 위한 위치 계산
            const positionedDevices = this.calculateDevicePositions(deviceNames);
            
            return {
              name: room,
              x,
              y,
              displayTypes,
              deviceNames: positionedDevices, // 위치가 계산된 장비 이름 배열
              tags: filteredTags, // 필터링된 태그 사용
            };
          });
        
        console.log('Final roomGroups:', this.roomGroups);
        
        // 아래 카드용 그룹핑(기존 유지)
        const grouped = {};
        this.filteredDisplayedTags.forEach(tag => {
          const key = tag.taglocation || '기타';
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(tag);
        });
        this.groups = Object.keys(grouped).map(key => ({ name: key, tags: grouped[key] }));
      } catch (error) {
        console.error('Error fetching tag data:', error);
      }
    },
    animateTag(tagMac) {
      this.animatingTags = [...this.animatingTags, tagMac];
      setTimeout(() => {
        this.animatingTags = this.animatingTags.filter(id => id !== tagMac);
      }, 2000);
    },
    onImageLoad(event) {
      console.log('Image loaded successfully:', {
        naturalWidth: event.target.naturalWidth,
        naturalHeight: event.target.naturalHeight,
        src: event.target.src
      });
      
      this.imageNaturalWidth = event.target.naturalWidth;
      this.imageNaturalHeight = event.target.naturalHeight;
      this.imageLoaded = true;
      this.updateContainerSize();
      this.recalculateTagPositions();
    },
    
    onImageError(event) {
      console.error('Image failed to load:', {
        src: event.target.src,
        error: event
      });
    },
    
    updateContainerSize() {
      this.$nextTick(() => {
        const container = this.$refs.mapContainer;
        if (container) {
          this.containerWidth = container.clientWidth;
          this.containerHeight = container.clientHeight;
        }
      });
    },
    handleResize() {
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.updateContainerSize();
        this.$nextTick(() => {
          this.fetchTagData();
        });
      }, 200);
    },
    recalculateTagPositions() {
      if (!this.imageLoaded || this.displayedTags.length === 0) return;
      const scale = this.imageScale;
      this.displayedTags = this.displayedTags.map(tag => {
        if (tag.baseX !== undefined && tag.baseY !== undefined) {
          const x = tag.baseX * scale.scaleX + scale.offsetX;
          const y = tag.baseY * scale.scaleY + scale.offsetY;
          return { ...tag, x, y };
        }
        return tag;
      });
    },
    getFloorTags(floor) {
      const group = this.groups.find(g => g.name === floor);
      return group ? group.tags : [];
    },
    isCurrentFloorRoom(room) {
      const rooms73 = [
        '3701','3702','3703','3704','3705','3706','3707','3709','3708','3710','3712','73보관실','7층_간이주방'
      ];
      const rooms74 = [
        '3751','3752','3753','3754','3755','3756','3757','3758','3759','3760','3762','74보관실','7층_간이주방'
      ];
      const rooms83 = [
        '3801','3802','3803','3804','3805','3806','3807','3808','3809','3810','3811','3812','3813','3814','3815','3816','3817','3818','3819','3820','3821','3822','3823','3824','3825','3826','3827','3828','3829','3830','3831','3832','3833','3834','3835','3836','83보관실', '83간호사실'
      ];
      
      if (this.sdaaFloor === '73') {
        return rooms73.includes(room);
      } else if (this.sdaaFloor === '74') {
        return rooms74.includes(room);
      } else if (this.sdaaFloor === '83') {
        return rooms83.includes(room);
      }
      return false;
    },
    getDeviceColor(deviceType) {
      // 직접 매칭
      if (this.deviceTypeColors[deviceType]) {
        return this.deviceTypeColors[deviceType];
      }
      
      // 대소문자 무시 매칭
      const lowerDeviceType = deviceType.toLowerCase();
      for (const [key, color] of Object.entries(this.deviceTypeColors)) {
        if (key.toLowerCase() === lowerDeviceType) {
          return color;
        }
      }
      
      // 기본 회색
      return '#888';
    },
    handleRoomClick(room) {
      // 타입별 devicename 리스트를 묶어서 출력
      const typeMap = {};
      room.tags.forEach(tag => {
        if (!typeMap[tag.devicetype]) typeMap[tag.devicetype] = [];
        if (tag.devicename && tag.devicename !== 'temp') typeMap[tag.devicetype].push(tag.devicename);
      });
      let msg = '';
      Object.entries(typeMap).forEach(([type, names]) => {
        msg += `${type} (${names.length})\n`;
        names.forEach(name => {
          msg += `${name}\n`;
        });
      });
      alert(msg.trim());
    },
    
    // 디바이스 타입 필터 토글
    toggleDeviceFilter(deviceType) {
      const index = this.selectedDeviceFilters.indexOf(deviceType);
      if (index > -1) {
        this.selectedDeviceFilters.splice(index, 1);
      } else {
        this.selectedDeviceFilters.push(deviceType);
      }
      this.applyDeviceFilters();
    },
    
    // 모든 디바이스 타입 필터 제거
    clearDeviceFilters() {
      this.selectedDeviceFilters = [];
      this.applyDeviceFilters();
    },
    
    // 디바이스 타입 필터 적용
    applyDeviceFilters() {
      // 필터가 적용된 roomGroups 업데이트
      this.updateFilteredRoomGroups();
    },
    
    // 필터링된 roomGroups 업데이트
    updateFilteredRoomGroups() {
      // 원본 데이터에서 필터링된 결과 생성
      this.fetchTagData();
    },
    
    // 장비 이름들의 위치를 스마트하게 계산하는 메서드 (반응형 지원 + 스케일링)
    calculateDevicePositions(devices) {
      if (!devices || devices.length === 0) return [];
      
      const positionedDevices = [];
      
      // 화면 크기에 따른 반응형 설정
      const screenWidth = window.innerWidth;
      let maxDevicesPerColumn, rowHeight, columnWidth, xOffset;
      
      if (screenWidth <= 768) {
        // 모바일 - 간격과 오프셋 대폭 줄임
        maxDevicesPerColumn = 6;
        rowHeight = 15;
        columnWidth = 40;
        xOffset = 20;
        console.log('Mobile settings applied'); // 디버깅용
      } else if (screenWidth <= 1024) {
        // 테블릿
        maxDevicesPerColumn = 8;
        rowHeight = 18;
        columnWidth = 50;
        xOffset = 30;
      } else {
        // 데스크톱 - 간격 대폭 줄임
        maxDevicesPerColumn = 15; // 더 많은 장비를 한 줄에
        rowHeight = 25;
        columnWidth = 70; // 50 → 30으로 대폭 줄임
        xOffset = 230; // 95 + 80 = 175
      }
      
      // 스케일 적용
      const scale = this.imageScale;
      const scaledRowHeight = rowHeight * scale.scaleY;
      const scaledColumnWidth = columnWidth * scale.scaleX;
      const scaledXOffset = xOffset * scale.scaleX;
      
      devices.forEach((device, index) => {
        // 세로로 먼저 쌓고, 다음 열로 넘어가기
        const column = Math.floor(index / maxDevicesPerColumn); // 열 계산 (0, 1, 2...)
        const row = index % maxDevicesPerColumn; // 행 계산 (0~maxDevicesPerColumn-1)
        
        // 장비 위치 설정 (방 라벨 아래에 배치)
        positionedDevices.push({
          ...device,
          positionX: scaledXOffset + (column * scaledColumnWidth), // 스케일링된 x 오프셋
          positionY: row * scaledRowHeight // 스케일링된 y 위치
        });
      });
      
      return positionedDevices;
    },
    scrollToWard() {
      // 병동별 스크롤 위치 설정 (가로 형태에 맞게 조정)
      const mapContainer = this.$refs.mapContainer;
      if (!mapContainer) return;
      
      let scrollX = 0;
      let scrollY = 0;
      
      if (this.sdaaFloor === '74') {
        scrollX = 800; // 74병동 영역으로 가로 스크롤
        scrollY = 0;
      } else if (this.sdaaFloor === '83') {
        scrollX = 1600; // 83병동 영역으로 가로 스크롤
        scrollY = 0;
      } else {
        scrollX = 0; // 73병동은 맨 왼쪽
        scrollY = 0;
      }
      
      mapContainer.scrollTo({
        left: scrollX,
        top: scrollY,
        behavior: 'smooth'
      });
    },
    
    // URL 쿼리 파라미터에서 필터 정보를 가져와서 자동 설정
    applyQueryFilters() {
      const query = this.$route.query;
      console.log('AssetMapSDAA - Received query params:', query);
      
      let hasChanges = false;
      
      if (query.ward) {
        // 병동 설정
        console.log('Setting ward to:', query.ward);
        this.sdaaFloor = query.ward;
        hasChanges = true;
      }
      
      if (query.deviceType) {
        // 디바이스 타입 필터 설정
        const deviceType = this.normalizeDeviceType(query.deviceType);
        console.log('Normalized deviceType:', deviceType, 'from:', query.deviceType);
        
        if (deviceType && !this.selectedDeviceFilters.includes(deviceType)) {
          console.log('Setting device filter to:', deviceType);
          this.selectedDeviceFilters = [deviceType];
          hasChanges = true;
        }
      }
      
      // 필터 적용 후 데이터 새로고침
      if (hasChanges) {
        console.log('Filters changed, refreshing data...');
        this.$nextTick(() => {
          this.fetchTagData();
        });
      }
    },
    
    // 디바이스 타입 이름 정규화 (PulseOxi -> PulseOxi, DVT -> DVT 등)
    normalizeDeviceType(deviceType) {
      console.log('Normalizing deviceType:', deviceType);
      
      const normalizedMap = {
        'PulseOxi': 'PulseOxi',
        'Pulse': 'PulseOxi',  // Pulse -> PulseOxi로 매핑
        'DVT': 'DVT',
        'Nebulizer': 'Nebulizer',
        'InfusionP': 'InfusionP',
        'PatientM': 'PatientM',
        'FeedingP': 'FeedingP',
        'VarioS': 'VarioS',
        'SCD_P': 'SCD_P',
        'HPO': 'HPO'
      };
      
      const normalized = normalizedMap[deviceType] || deviceType;
      console.log('Normalized result:', normalized);
      return normalized;
    },
    
    // 장비 이름을 3글자_숫자 형식으로 변환
    convertToShortName(originalName, deviceType) {
      // 디바이스 타입별 3글자 약어 매핑
      const typeAbbreviations = {
        'DVT': 'DVT',
        'VarioS': 'VAR',
        'Nebulizer': 'NEB',
        'InfusionP': 'INF',
        'PatientM': 'PAT',
        'FeedingP': 'FED',
        'PulseOxi': 'PUL',
        'SCD_P': 'SCD',
        'HPO': 'HPO'
      };
      
      // 디바이스 타입에서 3글자 약어 가져오기
      const typeAbbr = typeAbbreviations[deviceType] || deviceType.substring(0, 3).toUpperCase();
      
      // 원본 이름에서 숫자 추출
      const numberMatch = originalName.match(/\d+/);
      const number = numberMatch ? numberMatch[0] : '1';
      
      // 3글자_숫자 형식으로 반환
      return `${typeAbbr}_${number}`;
    }
  },
  created() {
    this.fetchTagData();
    // URL 쿼리 파라미터에서 필터 정보 가져오기
    this.applyQueryFilters();
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.updateContainerSize();
    console.log('dashboardMode prop:', this.dashboardMode);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    sdaaFloor() {
      this.fetchTagData();
      // 병동 변경 시 해당 영역으로 자동 스크롤
      this.$nextTick(() => {
        this.scrollToWard();
      });
    }
  }
};
</script>

<style scoped>
.asset-map.sdaa-map {
  position: relative;
  height: 100vh;
  width: 100%;
  overflow: hidden;
}

.asset-map.sdaa-map .map-container {
  position: relative;
  height: calc(100vh - 200px) !important; /* 뷰포트 높이에 맞춤 */
  width: 100%;
  margin: 0 auto;
  overflow: auto; /* 가로/세로 스크롤 모두 허용 */
  background: #eee;
}
.asset-map.sdaa-map .background-image.sdaa-image {
  width: auto; /* 원본 너비 유지 */
  height: auto; /* 원본 높이 유지 */
  max-width: 100%; /* 컨테이너를 넘지 않도록 제한 */
  max-height: 100%; /* 컨테이너를 넘지 않도록 제한 */
  object-fit: contain; /* 비율 유지하면서 컨테이너에 맞춤 */
  display: block; /* 인라인 요소 제거 */
}
.tag-marker {
  position: absolute;
  transform: translate(-50%, -50%);
  padding: 6px 10px;
  border-radius: 6px;
  background-color: rgba(0, 238, 255, 0.9);
  color: black;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
  z-index: 100;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  border: 1px solid rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}
.tag-moving {
  background-color: rgba(255, 165, 0, 0.9);
  transform: translate(-50%, -50%) scale(1.1);
  box-shadow: 0 4px 8px rgba(0,0,0,0.3);
  z-index: 101;
}
.tag-marker:hover {
  background-color: rgba(0, 200, 255, 0.9);
  transform: translate(-50%, -50%) scale(1.1);
  z-index: 102;
}
.tags-info-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
  padding: 10px;
  background-color: #f5f5f5;
  max-height: 120px;
  overflow-y: auto;
  box-sizing: border-box;
}
.floor-group {
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
}
.floor-group h3 {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}
.floor-group ul {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 150px;
  overflow-y: auto;
}
.floor-group li {
  padding: 4px 8px;
  margin: 2px 0;
  border-radius: 4px;
  font-size: 13px;
  transition: background-color 0.2s;
}
.floor-group li:hover {
  background-color: #f8f9fa;
}

/* 통합된 필터 섹션 - 상단에 고정되지만 메뉴에 가려지지 않음 */
.unified-filter-section {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: center;
  padding: 15px 0;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.unified-filter-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  width: 100%;
}

.ward-filter-row {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.ward-btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #aaa;
  background: #fff;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
}

.ward-btn:hover {
  background: #e9ecef;
}

.ward-btn.active {
  background: #00eaff;
  color: #222;
  border: 2px solid #00bcd4;
}
.room-marker-group {
  position: absolute;
  display: flex;
  flex-direction: column; /* 세로 방향으로 변경 */
  align-items: center;
  width: auto;
  height: auto; /* 높이를 자동으로 설정 */
  pointer-events: auto;
  z-index: 110;
  /* transform: translateY(-50%); 제거 - 정확한 위치에 배치 */
  cursor: pointer;
}
/* 장비 이름 플로팅 스타일 */
.device-names-floating {
  position: relative;
  height: clamp(120px, 22vh, 260px); /* 화면 높이에 비례하도록 조정 */
  min-width: clamp(120px, 20vw, 280px); /* 화면 너비에 비례하도록 조정 */
  margin-top: 0; /* 방 라벨과의 간격 제거 */
}

.device-name-tag {
  position: absolute;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: bold;
  color: white;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  transition: all 0.2s ease;
  min-width: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  border: 1px solid rgba(255,255,255,0.3);
  backdrop-filter: blur(2px);
  text-align: center;
}

.device-name-tag:hover {
  transform: scale(1.15);
  z-index: 200 !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.4);
  border-color: rgba(255,255,255,0.6);
}

.device-names-ellipsis {
  position: absolute;
  right: -5px;
  bottom: 0;
  font-size: 14px;
  color: #666;
  font-weight: bold;
}

.device-type-circles {
  position: relative;
  height: 18px;
  min-width: 30px;
}
.device-type-circle {
  position: absolute;
  top: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 1px 4px rgba(0,0,0,0.10);
  cursor: pointer;
  transition: transform 0.2s ease;
}
.device-type-circle:hover {
  transform: scale(1.2);
}
.device-type-ellipsis {
  font-size: 22px;
  color: #222;
  margin-left: 4px;
  font-weight: bold;
}
.room-label {
  position: relative; /* absolute에서 relative로 변경 */
  /* top: 0px; 제거 - 상대적 위치 사용 */
  left: 50%;
  transform: translateX(-50%);
  font-size: 14px;
  font-weight: bold;
  color: #333;
  background: rgba(255,255,255,0.8);
  padding: 2px 8px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  pointer-events: auto;
  z-index: 120;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-bottom: 5px; /* 장비 이름들과의 간격 */
}
.room-label:hover {
  background: rgba(255,255,255,0.95);
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
.device-filter-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  max-width: 800px;
}

.filter-btn {
  padding: 4px 8px;
  border: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  color: #333;
  border-radius: 6px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.2s;
  min-width: 55px;
  text-align: center;
  margin: 1px;
}

.filter-btn:hover {
  background-color: #e9ecef;
}

.filter-btn.active {
  background-color: #4e73df;
  color: white;
  border-color: #4e73df;
}

.clear-btn {
  background-color: #dc3545;
  color: white;
}

.clear-btn:hover {
  background-color: #c82333;
}

.legend-fixed-area {
  position: fixed;
  top: 120px;
  left: 420px;
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.device-type-legend {
  background: rgba(255,255,255,0.95);
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  padding: 12px 18px 10px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 180px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #444;
}
.legend-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.10);
}
.legend-label {
  font-size: 14px;
  color: #333;
}
.horizontal-circles {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  position: static !important;
  height: auto !important;
  overflow-x: auto;
  max-width: 120px;
}
.horizontal-circles .device-type-circle {
  position: static !important;
  left: unset !important;
  margin-right: 4px;
}

/* 툴팁 스타일 */
.marker-tooltip {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: bold;
  white-space: nowrap;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  pointer-events: none; /* 툴팁 자체는 클릭 이벤트를 받지 않도록 */
}
.tooltip-room-name {
  margin-bottom: 4px;
  font-size: 16px;
  color: #fff;
}
.tooltip-device-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.tooltip-device-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #eee;
}
.tooltip-device-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}
.tooltip-device-name {
  font-weight: bold;
}
.tooltip-device-count {
  font-size: 12px;
  color: #ccc;
}
.asset-map-flex-col {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
}
.map-container {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  overflow: auto;
  /* 스크롤바 스타일링 */
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 transparent;
}

/* 웹킷 브라우저용 스크롤바 스타일 */
.map-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.map-container::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.map-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.map-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.5);
}

.map-container::-webkit-scrollbar-corner {
  background: rgba(0, 0, 0, 0.1);
}
.tags-info-section {
  max-height: 80px;
  overflow-y: auto;
  width: 100%;
  background: #f5f5f5;
  z-index: 2;
  box-sizing: border-box;
  position: relative;
}

/* 반응형 디자인 - 테블릿과 컴퓨터 화면 일치 */
@media screen and (max-width: 1024px) {
  /* 테블릿 화면에서 장비 이름 태그 크기 조정 */
  .device-name-tag {
    font-size: 10px;
    padding: 2px 6px;
    min-width: 35px;
  }
  
  /* 장비 이름 플로팅 영역 조정 */
  .device-names-floating {
    height: 200px;
    min-width: 200px;
  }
  
  /* 방 라벨 크기 조정 */
  .room-label {
    font-size: 12px;
    padding: 1px 6px;
  }
}

@media screen and (max-width: 768px) {
  /* 모바일 화면에서 더 작은 크기와 간격 */
  .device-name-tag {
    font-size: 8px;
    padding: 1px 3px;
    min-width: 25px;
  }
  
  .device-names-floating {
    height: 120px;
    min-width: 120px;
  }
  
  .room-label {
    font-size: 10px;
    padding: 1px 3px;
  }
}

/* 큰 화면에서의 최적화 */
@media screen and (min-width: 1600px) {
  .device-name-tag {
    font-size: 12px;
    padding: 4px 10px;
    min-width: 45px;
  }
  
  .device-names-floating {
    height: 250px;
    min-width: 280px;
  }
  
  .room-label {
    font-size: 15px;
    padding: 3px 10px;
  }
}
</style> 